// apps/client/src/types/loanBookView.ts
//
// Typed contract for the scoped, composed loan book value shown on the Broker
// Performance Report (and its shared/public twin).
//
// Background: docs/handovers/2026-08-04-LOAN-BOOK-VS-LMG-RECONCILIATION.md.
// A single unlabelled "Loan Book Value" card read $78.43M where the Loan Market
// report for the same broker read $64.80M. The number was not wrong, it was
// unlabelled: it aggregated every lending entity, carried loans that had stopped
// paying trail, and blended lender-reported balances with estimated values.
//
// Two invariants this contract exists to enforce:
//   1. Money is integer cents. Convert only at presentation.
//   2. A loan book figure is never rendered without the scope it was measured in.

/** Which entity scope the figures were measured in. */
export type LoanBookMode = 'primary' | 'all';

export interface LoanBookEntity {
  /** Lending entity code, for example 'ENTM100'. */
  code: string;
  /** Human label for the entity, for example 'Mornington'. */
  label: string;
}

export interface LoanBookTier {
  cents: number;
  loanCount: number;
}

export interface LoanBookFreshness {
  entityCode: string;
  /** ISO date string, 'YYYY-MM-DD', for the position this entity last filed. */
  asAt: string;
  /** True when this entity has not filed for the current month. */
  stale: boolean;
}

export interface LoanBookView {
  mode: LoanBookMode;
  /** Absent means no primary entity is configured. Never guess one. */
  primaryEntity?: LoanBookEntity;
  /** The trail-paying book. This is the headline. */
  headlineCents: number;
  /** Value held outside the primary entity. Always 0 when mode === 'all'. */
  otherEntitiesCents: number;
  /** Settled but not yet reached a first trail cycle. Outside the headline. */
  newSettlements: LoanBookTier;
  /** Loans valued from a lender-reported outstanding balance. */
  balanceTier: LoanBookTier;
  /** Loans valued from the original settled amount because no balance was ever reported. */
  fallbackTier: LoanBookTier;
  /** Acquired trail books, always estimated. Absent when the broker holds none. */
  acquiredTrail?: LoanBookTier;
  /** Loans making up the headline. */
  activeLoans: number;
  freshness: LoanBookFreshness[];
}

/**
 * The scope label that MUST accompany every rendered loan book figure.
 *
 * Root cause of the 2026-08-04 incident was a loan book number with no scope
 * label attached, so this is a hard requirement rather than a nicety. Returns a
 * label for every reachable state, including "primary requested but not
 * configured", which falls back to the all-entities wording and is surfaced
 * separately by `isPrimaryUnset`.
 */
export function scopeLabel(view: Pick<LoanBookView, 'mode' | 'primaryEntity'>): string {
  if (view.mode === 'primary' && view.primaryEntity) {
    return `Primary loan book: ${view.primaryEntity.label} (${view.primaryEntity.code})`;
  }
  return 'All loan books';
}

/** True when the caller asked for (or the toggle offers) a primary that is not configured. */
export function isPrimaryUnset(view: Pick<LoanBookView, 'primaryEntity'>): boolean {
  return !view.primaryEntity;
}

/**
 * True when value sits outside the primary entity and would otherwise be
 * invisible. Drives the persistent "+ $X in other loan books" secondary line.
 */
export function hasHiddenOtherEntityValue(
  view: Pick<LoanBookView, 'mode' | 'otherEntitiesCents'>,
): boolean {
  return view.mode === 'primary' && view.otherEntitiesCents > 0;
}

/** Entities whose latest filing is older than the current month. */
export function staleEntities(view: Pick<LoanBookView, 'freshness'>): LoanBookFreshness[] {
  return view.freshness.filter((f) => f.stale);
}

/**
 * True when the aggregate blends more than one as-at date. Entities file at
 * different times, so an "All loan books" total is routinely a mixed-date figure
 * and the screen has to say so.
 */
export function hasMixedAsAtDates(view: Pick<LoanBookView, 'freshness'>): boolean {
  const distinct = new Set(view.freshness.map((f) => f.asAt));
  return distinct.size > 1;
}
